/**
 * Bajar el dibujo de un objeto para editarlo afuera.
 *
 * Pedido de Daniel (12-oct-2026): «que cuando uno seleccione un objeto pueda
 * descargarlo en SVG o PDF, por si quiero editar y hacer uno diferente pero con
 * la misma gráfica». O sea: el catálogo deja de ser una calle de una sola mano.
 * Hasta hoy se podía meter un dibujo (`importar-simbolo`) y no sacarlo, así que
 * partir de una pieza existente obligaba a redibujarla desde cero.
 *
 * El ciclo queda cerrado y simétrico: lo que baja de acá lo vuelve a leer
 * `desdeArchivo` sin pasos intermedios.
 *
 * Dos formatos porque resuelven cosas distintas:
 *
 *  - **SVG** es el fiel. Sale el mismo markup que pinta la vitrina, con sus
 *    rellenos y sus grosores, y abre igual en Illustrator, Inkscape o Figma.
 *  - **PDF** es el que entra a un plano. Va por `desdeSVG`, así que llega
 *    reducido a contornos (`M/L/C/Z`) y PIERDE LOS RELLENOS — para re-dibujar
 *    la pieza eso no estorba, pero conviene saberlo antes de extrañarlos.
 *
 * El PDF se escribe a mano, sin librería. No es terquedad: el normalizador de
 * `importar-simbolo` ya dejó todo en mover / recta / cúbica / cerrar, que son
 * exactamente los operadores `m`/`l`/`c`/`h` del PDF. Traer `jspdf` serían
 * ~350 kB de bundle para lo que acá resuelven cuarenta líneas.
 */
import { renderToStaticMarkup } from 'react-dom/server';

// `TRAZO` llega con alias: suelto se confunde a la vista con el TIPO `Trazo`,
// que también vive en este archivo y es otra cosa por completo.
import {
  TINTA_ELECTRICO, TINTA_MUEBLE, TRAZO as GROSOR_TRAZO, dibujoDe,
} from './muebles';
import type { ModeloMueble } from './muebles';
import { desdeSVG } from './importar-simbolo';

/**
 * Puntos PostScript por metro.
 *
 * Un punto es 1/72 de pulgada, así que un metro son 72/0,0254. Esto es lo que
 * hace que la pieza abra a TAMAÑO REAL: un mueble de 80 cm mide 80 cm en la
 * regla del programa que lo abra, no «80 unidades».
 */
const PT_POR_METRO = 72 / 0.0254;

/**
 * Nombre de archivo sin sorpresas: sin acentos, sin barras, sin espacios.
 *
 * Los acentos se parten con `NFD` y se barren por rango (`̀-ͯ`) en vez
 * de listar los caracteres: escritos literales son marcas invisibles que
 * cualquier editor o copia-pega puede comerse sin que se note.
 */
function aNombreDeArchivo(nombre: string): string {
  return (nombre.normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase()
    || 'objeto');
}

/** Redondeo a 4 decimales, el mismo que usa el importador. */
const n4 = (v: number) => Math.round(v * 1e4) / 1e4;

/**
 * El dibujo de una pieza como SVG suelto, a tamaño real.
 *
 * Sale del MISMO `dibujoDe` que pinta la vitrina y el plano, así que una pieza
 * con dibujo propio y una de fábrica bajan por el mismo camino y no hay dos
 * versiones de la gráfica que puedan separarse con el tiempo.
 *
 * El `viewBox` es la caja exacta de la pieza —sin aire— para que la medida del
 * archivo SEA la medida del mueble; `overflow="visible"` evita que el medio
 * grosor de línea que sobresale en el borde quede recortado.
 */
export function aSVG(m: ModeloMueble): string {
  const cuerpo = renderToStaticMarkup(dibujoDe(m, m.w, m.h));
  const color = m.electrico ? TINTA_ELECTRICO : TINTA_MUEBLE;
  return '<?xml version="1.0" encoding="UTF-8"?>\n'
    + `<!-- ${m.nombre} · ${Math.round(m.w * 100)}×${Math.round(m.h * 100)} cm`
    + ` · ArquiFlow -->\n`
    + `<svg xmlns="http://www.w3.org/2000/svg"`
    + ` width="${n4(m.w * 100)}cm" height="${n4(m.h * 100)}cm"`
    + ` viewBox="0 0 ${n4(m.w)} ${n4(m.h)}" overflow="visible"`
    + ` color="${color}">\n  ${cuerpo}\n</svg>\n`;
}

// ─── PDF ────────────────────────────────────────────────────────────────────

/** Los números del PDF no admiten notación exponencial: `1e-7` rompe el parser. */
const pdfNum = (v: number) => (Math.round(v * 1e3) / 1e3).toFixed(3).replace(/\.?0+$/, '') || '0';

/**
 * Los caminos del trazo como operadores de pintado del PDF.
 *
 * La correspondencia es uno a uno porque el importador ya convirtió arcos y
 * cuadráticas a cúbicas: `M`→`m`, `L`→`l`, `C`→`c`, `Z`→`h`. Cada camino se
 * cierra con su propio `S` (stroke) en vez de acumular todos y pintar al final,
 * porque un `h` de un camino arrastraría al siguiente.
 */
function aOperadores(d: string): string {
  const ops: string[] = [];
  // Se parte por LETRA, no por espacio. El normalizador escribe el comando
  // PEGADO a su primer número (`M0.1 0.2`, no `M 0.1 0.2`), así que partir por
  // espacio deja `M0.1` como primer token, que no es ningún comando conocido:
  // el `d` entero se descarta en silencio y el PDF sale estructuralmente
  // correcto pero sin una sola línea adentro. Costó un banco descubrirlo.
  for (const [, c, crudo] of d.matchAll(/([MLCZ])([^MLCZ]*)/g)) {
    if (c === 'Z') { ops.push('h'); continue; }
    const n = (crudo.match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) ?? []).map(Number);
    const letra = c === 'M' ? 'm' : c === 'L' ? 'l' : 'c';
    const paso = c === 'C' ? 6 : 2;
    for (let i = 0; i + paso <= n.length; i += paso) {
      ops.push(`${n.slice(i, i + paso).map(pdfNum).join(' ')} ${letra}`);
    }
  }
  return ops.length ? `${ops.join('\n')}\nS\n` : '';
}

/**
 * El dibujo de una pieza como PDF de una página, a tamaño real.
 *
 * Pasa por `desdeSVG` en vez de leer `m.trazo` directo a propósito: así las
 * piezas de fábrica —que no tienen `trazo`, sino un símbolo dibujado en
 * código— también bajan, y por el mismo camino que las importadas.
 */
export function aPDF(m: ModeloMueble): string {
  const t = desdeSVG(aSVG(m));
  const [cx, cy, cw, ch] = t.caja;
  const W = m.w * PT_POR_METRO;
  const H = m.h * PT_POR_METRO;

  // Una sola matriz hace tres cosas de un saque: lleva las unidades del dibujo
  // a puntos, encuadra la caja en la página, y VOLTEA la y —el PDF crece hacia
  // arriba y el SVG hacia abajo—. Con esto los puntos se escriben tal cual.
  const kx = cw > 0 ? W / cw : PT_POR_METRO;
  const ky = ch > 0 ? H / ch : PT_POR_METRO;
  const cabeza = `${pdfNum(kx)} 0 0 ${pdfNum(-ky)} ${pdfNum(-cx * kx)} ${pdfNum(H + cy * ky)} cm\n`
    // El grosor va en unidades del DIBUJO, no en puntos: la matriz de arriba ya
    // lo escala. Y resulta que el `viewBox` del SVG está en metros, las mismas
    // unidades en que está escrito `GROSOR_TRAZO`, así que va tal cual.
    + `${pdfNum(GROSOR_TRAZO)} w\n`
    + '1 J 1 j\n';   // puntas y uniones redondeadas, como en pantalla

  const flujo = cabeza + t.d.map(aOperadores).join('');

  // Un PDF mínimo: catálogo, árbol de páginas, la página y su contenido. La
  // tabla `xref` exige el desplazamiento en BYTES de cada objeto, así que se
  // arma acumulando a medida que se concatena, no al final.
  const objetos = [
    '<</Type/Catalog/Pages 2 0 R>>',
    '<</Type/Pages/Kids[3 0 R]/Count 1>>',
    `<</Type/Page/Parent 2 0 R/MediaBox[0 0 ${pdfNum(W)} ${pdfNum(H)}]`
      + '/Contents 4 0 R/Resources<<>>>>',
    `<</Length ${new TextEncoder().encode(flujo).length}>>\nstream\n${flujo}endstream`,
  ];

  let pdf = '%PDF-1.4\n';
  const donde: number[] = [];
  objetos.forEach((o, i) => {
    donde.push(new TextEncoder().encode(pdf).length);
    pdf += `${i + 1} 0 obj\n${o}\nendobj\n`;
  });
  const xref = new TextEncoder().encode(pdf).length;
  pdf += `xref\n0 ${objetos.length + 1}\n0000000000 65535 f \n`
    + donde.map((d) => `${String(d).padStart(10, '0')} 00000 n \n`).join('')
    + `trailer\n<</Size ${objetos.length + 1}/Root 1 0 R>>\n`
    + `startxref\n${xref}\n%%EOF\n`;
  return pdf;
}

/**
 * Baja la pieza al disco.
 *
 * El `revokeObjectURL` no es higiene opcional: sin él cada descarga deja los
 * bytes del archivo vivos en memoria hasta que se recargue la página, y acá se
 * baja de a muchas piezas seguidas.
 */
export function bajarObjeto(m: ModeloMueble, formato: 'svg' | 'pdf'): void {
  const texto = formato === 'svg' ? aSVG(m) : aPDF(m);
  const tipo = formato === 'svg' ? 'image/svg+xml' : 'application/pdf';
  const url = URL.createObjectURL(new Blob([texto], { type: tipo }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${aNombreDeArchivo(m.nombre)}.${formato}`;
  a.click();
  URL.revokeObjectURL(url);
}
